import { Button, Chip, Input, Picker, Switch } from '@patchbay/ui'
import { useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import * as React from 'react'
import type { Option, ProfitMeta } from '../../lib/api/generated'
import { searchQuery } from '../../lib/api/queries'
import { monthKey, type PeriodKind, type ProfitSearch } from './api'

export const selectClass =
  'h-8 min-w-0 rounded-md border border-input bg-card px-2 text-sm text-foreground outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25 disabled:opacity-60'

const PERIOD_LABEL: Record<PeriodKind, string> = { month: 'Month', quarter: 'Quarter', ytd: 'Year to date', custom: 'Custom' }

function OptionSelect({
  label,
  value,
  options,
  onChange,
  allLabel = 'All',
}: {
  label: string
  value: string | undefined
  options: Option[] | undefined
  onChange: (value: string | undefined) => void
  allLabel?: string
}) {
  return (
    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      {label}
      <Picker
        aria-label={label}
        className={selectClass}
        value={value ?? ''}
        disabled={!options}
        title={options ? undefined : 'Options unavailable (filter options did not load)'}
        onChange={(e) => onChange(e.currentTarget.value || undefined)}
      >
        <option value="">{options ? allLabel : 'Unavailable'}</option>
        {/* A value from a shared link stays selectable even when the options list lacks it. */}
        {value && options && !options.some((o) => o.value === value) ? <option value={value}>{value}</option> : null}
        {(options ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Picker>
    </div>
  )
}

/** Customer picker over the shared search endpoint (Conduit customers). A failed search says so; it never says "no match". */
export function CustomerPicker({
  onPick,
  label = 'Customer',
  autoFocus,
}: {
  onPick: (hit: { id: string; label: string }) => void
  label?: string
  autoFocus?: boolean
}) {
  const queryClient = useQueryClient()
  const choices = React.useRef(new Map<string, { id: string; label: string }>())
  return (
    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      <span>{label}</span>
      <Picker
        ariaLabel={label}
        value=""
        options={[]}
        autoFocus={autoFocus}
        placeholder="Search customers"
        emptyMessage="Type at least 2 characters"
        errorMessage="Customer search unreachable"
        className="h-8 w-48"
        loadOptions={async (query, signal) => {
          const term = query.trim()
          if (term.length < 2) return []
          const hits = await queryClient.fetchQuery(searchQuery(term))
          if (signal.aborted) return []
          const customers = hits.filter((hit) => hit.kind === 'customer')
          choices.current = new Map(customers.map((hit) => [hit.id, { id: hit.id, label: hit.label }]))
          return customers.map((hit) => ({ value: hit.id, label: hit.label, description: hit.sublabel ?? undefined }))
        }}
        onValueChange={(value) => {
          const hit = choices.current.get(value)
          if (hit) onPick(hit)
        }}
      />
    </div>
  )
}

/** The sticky filter bar shared by every tab. Every control writes the URL (a view can be shared by its address). */
export function FilterBar({
  search,
  onChange,
  meta,
  manager,
}: {
  search: ProfitSearch
  onChange: (patch: Partial<ProfitSearch>) => void
  meta: ProfitMeta | undefined
  manager: boolean
}) {
  const period = search.period ?? 'month'
  const anchor = search.month ?? monthKey(new Date())
  return (
    <search
      aria-label="Profitability filters"
      className="sticky top-0 z-20 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-background/95 px-5 py-2.5"
    >
      {/* biome-ignore lint/a11y/noLabelWithoutControl: Picker renders the nested combobox input. */}
      <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        Period
        <Picker
          aria-label="Period"
          className={selectClass}
          value={period}
          onChange={(e) => onChange({ period: e.currentTarget.value as PeriodKind })}
        >
          {(Object.keys(PERIOD_LABEL) as PeriodKind[]).map((k) => (
            <option key={k} value={k}>
              {PERIOD_LABEL[k]}
            </option>
          ))}
        </Picker>
      </label>
      {period === 'custom' ? (
        <>
          <Input
            type="month"
            aria-label="From month"
            className="h-8 w-36"
            value={search.from ?? anchor}
            onChange={(e) => onChange({ from: e.currentTarget.value || undefined })}
          />
          <Input
            type="month"
            aria-label="To month"
            className="h-8 w-36"
            value={search.to ?? search.from ?? anchor}
            onChange={(e) => onChange({ to: e.currentTarget.value || undefined })}
          />
        </>
      ) : (
        <Input
          type="month"
          aria-label={period === 'quarter' ? 'Month in quarter' : period === 'ytd' ? 'Through month' : 'Month'}
          className="h-8 w-36"
          value={anchor}
          onChange={(e) => onChange({ month: e.currentTarget.value || undefined })}
        />
      )}
      {/* biome-ignore lint/a11y/noLabelWithoutControl: Picker renders the nested combobox input. */}
      <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        Basis
        <Picker
          aria-label="Basis"
          className={selectClass}
          value={search.basis ?? 'transaction'}
          onChange={(e) => onChange({ basis: e.currentTarget.value === 'close' ? 'close' : undefined })}
        >
          <option value="transaction">Transaction date</option>
          <option value="close">Case close date</option>
        </Picker>
      </label>
      {/* biome-ignore lint/a11y/noLabelWithoutControl: Picker renders the nested combobox input. */}
      <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        Books org
        <Picker
          aria-label="Books org"
          className={selectClass}
          value={search.org ?? ''}
          onChange={(e) => onChange({ org: (e.currentTarget.value || undefined) as ProfitSearch['org'] })}
        >
          <option value="">Both</option>
          <option value="ewee">Ewee</option>
          <option value="smartpbx">SmartPBX</option>
        </Picker>
      </label>
      {search.customer ? (
        <span className="flex items-center gap-2">
          <Chip tone="primary" size="lg">
            {search.customer_name ?? 'Customer'}
            <button
              type="button"
              aria-label="Clear customer"
              className="cursor-pointer rounded hover:bg-primary/10"
              onClick={() => onChange({ customer: undefined, customer_name: undefined, children: undefined })}
            >
              <X className="size-3" />
            </button>
          </Chip>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Switch
              aria-label="Include child accounts"
              checked={Boolean(search.children)}
              onCheckedChange={(v) => onChange({ children: v || undefined })}
            />
            Include child accounts
          </label>
        </span>
      ) : (
        <CustomerPicker onPick={(hit) => onChange({ customer: hit.id, customer_name: hit.label })} />
      )}
      <OptionSelect label="Contract kind" value={search.kind} options={meta?.contract_kinds} onChange={(kind) => onChange({ kind })} />
      {/* biome-ignore lint/a11y/noLabelWithoutControl: Picker renders the nested combobox input. */}
      <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        Coverage
        <Picker
          aria-label="Coverage domain"
          className={selectClass}
          value={search.domain ?? ''}
          onChange={(e) => onChange({ domain: (e.currentTarget.value || undefined) as ProfitSearch['domain'] })}
        >
          <option value="">All</option>
          <option value="phone">Phone</option>
          <option value="it">IT</option>
          <option value="macd">MACD</option>
        </Picker>
      </label>
      {manager ? <OptionSelect label="Tech" value={search.tech} options={meta?.techs} onChange={(tech) => onChange({ tech })} /> : null}
      <OptionSelect
        label="Contractor"
        value={search.contractor}
        options={meta?.contractors}
        onChange={(contractor) => onChange({ contractor })}
      />
      <OptionSelect label="Department" value={search.dept} options={meta?.departments} onChange={(dept) => onChange({ dept })} />
      <OptionSelect
        label="Ticket kind"
        value={search.ticket_kind}
        options={meta?.ticket_kinds}
        onChange={(ticket_kind) => onChange({ ticket_kind })}
      />
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Switch
          aria-label="Quotable figures only"
          checked={Boolean(search.quotable)}
          onCheckedChange={(v) => onChange({ quotable: v || undefined })}
        />
        Quotable figures only
      </label>
      <Button
        variant="link"
        size="sm"
        className="ml-auto"
        onClick={() =>
          onChange({
            period: undefined,
            month: undefined,
            from: undefined,
            to: undefined,
            basis: undefined,
            org: undefined,
            customer: undefined,
            customer_name: undefined,
            children: undefined,
            kind: undefined,
            domain: undefined,
            tech: undefined,
            contractor: undefined,
            dept: undefined,
            ticket_kind: undefined,
            quotable: undefined,
            quick: undefined,
          })
        }
      >
        Clear filters
      </Button>
    </search>
  )
}
